import { Formik, Form } from "formik";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Divider, Grid, Header, Segment } from "semantic-ui-react";
import * as Yup from "yup";
import { toast } from "react-toastify";
import {
  addTransClientInDb,
  updatePartyInDb,
} from "../../../app/firestore/firestoreService";
import { closeModal } from "../../../app/common/modals/modalSlice";
import ModalWrapper from "../../../app/common/modals/modalWrapper";
import MySelectInput from "../../../app/common/form/MySelectInput";
import {
  createClientRoleOptions,
  createRestrictedEmails,
  partyIsSeller,
} from "../../../app/common/util/util";
import FormAddress from "../../../app/common/form/FormAddress";
import FormPeopleAutofill from "../../../app/common/form/FormPeopleAutofill";
import MyCheckbox from "../../../app/common/form/MyCheckbox";
import MyTextInput from "../../../app/common/form/MyTextInput";
import { useMediaQuery } from "react-responsive";
import { Popup, Icon } from "semantic-ui-react";
import { useFormikContext } from "formik";
import { useEffect } from "react";

// Helper function to create signer name format
const createSignerName = (firstName, middleName, lastName, authorityTitle) => {
  if (!firstName && !lastName) return "";

  let name = "";
  if (firstName) name += firstName;
  if (middleName) name += (name ? " " : "") + middleName;
  if (lastName) name += (name ? " " : "") + lastName;
  if (authorityTitle) name += ", " + authorityTitle;

  return name;
};

// Helper function to update primary party's sharedEmailClients when secondary/tertiary client shares email
async function updatePrimaryPartySharedEmailClients(
  clientValues,
  savedClientId,
  allParties
) {
  console.log(
    "updatePrimaryPartySharedEmailClients called with: ",
    clientValues,
    savedClientId
  );
  const role = clientValues.role;
  const isSecondaryOrTertiary =
    role === "Buyer 2" ||
    role === "Buyer 3" ||
    role === "Seller 2" ||
    role === "Seller 3";

  if (!isSecondaryOrTertiary) {
    console.log("Not secondary/tertiary, returning");
    return;
  }

  // Determine the primary role
  const primaryRole = role.startsWith("Buyer") ? "Buyer" : "Seller";
  const primaryParty = allParties.find((p) => p.role === primaryRole);

  if (!primaryParty) {
    console.log("Primary party not found for role:", primaryRole);
    return;
  }

  console.log("Found primary party:", primaryParty);

  // Get current sharedEmailClients array or initialize empty
  let sharedEmailClients = primaryParty.sharedEmailClients || [];
  console.log("Current sharedEmailClients:", sharedEmailClients);

  // Remove any existing entry for this party's role
  sharedEmailClients = sharedEmailClients.filter((item) => item.role !== role);

  // If checkbox is checked, add the new entry and copy primary's email to secondary/tertiary
  if (clientValues.sharesEmailWithPrimary) {
    console.log("Checkbox is checked, adding to sharedEmailClients");
    sharedEmailClients.push({
      role: role,
      odPartyId: savedClientId,
    });

    // Also update the secondary/tertiary party's email to match the primary party's email
    if (primaryParty.email) {
      console.log(
        `Copying primary party's email (${primaryParty.email}) to secondary/tertiary party`
      );
      // For newly added clients, the party might not be in allParties yet
      // so we update directly by ID
      await updatePartyInDb({ id: savedClientId, role: role }, { email: primaryParty.email });
      console.log("Secondary/tertiary party email updated successfully");
    }
  } else {
    console.log(
      "Checkbox is NOT checked, sharesEmailWithPrimary:",
      clientValues.sharesEmailWithPrimary
    );
    // Don't modify the email here - the form submission already handles
    // updating the email to whatever the user entered. We only need to
    // update the sharedEmailClients array (which was already done above
    // by filtering out this party's role).
  }

  console.log("Updated sharedEmailClients:", sharedEmailClients);
  // Update the primary party
  await updatePartyInDb(primaryParty, { sharedEmailClients });
  console.log("Primary party updated successfully");
}

export default function TransactionClientAddForm({
  client,
  transaction,
  people,
}) {
  const dispatch = useDispatch();
  const { allParties } = useSelector((state) => state.transaction);
  const { currentUserProfile } = useSelector((state) => state.profile);
  const isMobile = useMediaQuery({ query: "(max-width:768px)" });

  const clientRoleOptions = createClientRoleOptions(client, transaction);

  const primaryClientEmail = transaction?.client?.email || "";

  let initialValues = {
    type: "Client",
    role: "",
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    phone: "",
    address: {
      street: "",
      unit: "",
      city: "",
      state: "",
      zipcode: "",
    },
    isTrust: false,
    entityName: "",
    authorityTitle: "",
    signerName: "",
    personSelected: "",
    userId: transaction.userId,
    hasPoa: false,
    poaFullName: "",
    poaTitle: "",
    poaDisplayOption: "client_by_poa",
    poaCustomDisplay: "",
    sharedEmailClients: [],
    sharesEmailWithPrimary: false,
  };

  const [isTrustField, setIsTrustField] = useState(
    initialValues.isTrust ? true : false
  );
  const [hasPoaField, setHasPoaField] = useState(
    initialValues.hasPoa ? true : false
  );
  const [selectedRole, setSelectedRole] = useState(initialValues.role);

  // Watcher component for role changes
  function RoleWatcher({ role }) {
    React.useEffect(() => {
      setSelectedRole(role);
    }, [role]);
    return null;
  }

  // Watcher component for signer name updates
  function SignerNameWatcher() {
    const { values, setFieldValue } = useFormikContext();

    useEffect(() => {
      if (values.isTrust) {
        const signerName = createSignerName(
          values.firstName,
          values.middleName,
          values.lastName,
          values.authorityTitle || ""
        );
        setFieldValue("signerName", signerName);
      } else {
        setFieldValue("signerName", "");
      }
    }, [
      values.firstName,
      values.middleName,
      values.lastName,
      values.authorityTitle,
      values.isTrust,
      setFieldValue,
    ]);

    return null;
  }

  const validationSchema = Yup.object({
    lastName: Yup.string().required("You must provide a last name"),
    email: Yup.string()
      .email("Must be a valid email")
      .test(
        "email-restriction",
        "Can't use same email for different parties",
        function (value) {
          if (!value) return true; // Allow empty email

          const { role, sharesEmailWithPrimary } = this.parent;

          // If this is a secondary/tertiary client sharing email with primary, allow it
          const isSecondaryOrTertiary =
            role === "Buyer 2" ||
            role === "Buyer 3" ||
            role === "Seller 2" ||
            role === "Seller 3";

          if (isSecondaryOrTertiary && sharesEmailWithPrimary) {
            // Find the primary party
            const primaryRole = role.startsWith("Buyer") ? "Buyer" : "Seller";
            const primaryParty = allParties?.find(
              (p) => p.role === primaryRole
            );

            // Allow if email matches primary party's email
            if (primaryParty && value === primaryParty.email) {
              return true;
            }
          }

          // Otherwise, check against restricted emails
          const restrictedEmails = createRestrictedEmails(
            currentUserProfile,
            transaction,
            allParties
          );

          return !restrictedEmails.includes(value);
        }
      ),
    role: Yup.string().required("You must provide a role"),
    poaFullName: Yup.string().when("hasPoa", {
      is: true,
      then: (schema) => schema.required("POA full name is required"),
    }),
    poaTitle: Yup.string().when("hasPoa", {
      is: true,
      then: (schema) => schema.required("POA title is required"),
    }),
    poaCustomDisplay: Yup.string().when(["hasPoa", "poaDisplayOption"], {
      is: (hasPoa, poaDisplayOption) => hasPoa && poaDisplayOption === "custom",
      then: (schema) => schema.required("Custom display text is required"),
    }),
  });

  const poaDisplayOptions = [
    {
      key: "client_by_poa",
      value: "client_by_poa",
      text: "Client's name, by POA's name, as Attorney-in-Fact",
    },
    {
      key: "poa_for_client",
      value: "poa_for_client",
      text: "POA's name, as Attorney-in-Fact for Client's name",
    },
    {
      key: "custom",
      value: "custom",
      text: "Custom display (enter below)",
    },
  ];

  return (
    <ModalWrapper>
      <Segment clearing>
        <div className="medium horizontal margin small top margin">
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={async (values, { setSubmitting }) => {
              try {
                console.log("Start client add form submit");
                const newClientId = await addTransClientInDb(
                  transaction,
                  values
                );
                console.log("New client created with ID:", newClientId);

                // Update primary party's sharedEmailClients if applicable
                await updatePrimaryPartySharedEmailClients(
                  values,
                  newClientId,
                  allParties
                );
                console.log("Client add form submit completed");

                setSubmitting(false);
                toast.success("Client successfully added");
                dispatch(
                  closeModal({
                    modalType: "TransactionClientAddForm",
                  })
                );
              } catch (error) {
                toast.error(error.message);
                setSubmitting(false);
              }
            }}
          >
            {({ isSubmitting, dirty, isValid, values }) => (
              <Form className="ui form">
                <RoleWatcher role={values.role} />
                <SignerNameWatcher />
                <Header size="large" color="blue">
                  Add Client
                </Header>
                <Divider />
                <Grid>
                  <Grid.Column mobile={16} computer={5}>
                    <MySelectInput
                      name="role"
                      placeholder="Role"
                      options={clientRoleOptions}
                    />
                  </Grid.Column>
                </Grid>
                <Divider />
                <FormPeopleAutofill
                  people={people}
                  type={"Client"}
                  setIsTrustField={setIsTrustField}
                />
                <br />
                <MyCheckbox
                  name="isTrust"
                  label="This client is a company, trust, or other entity"
                  onClick={() => setIsTrustField(!isTrustField)}
                />
                {isTrustField && (
                  <>
                    <MyTextInput
                      name="entityName"
                      label="Company, trust, or entity name"
                    />
                    <MyTextInput
                      name="authorityTitle"
                      label="Authority title (e.g., Manager, Trustee, President, Member)"
                      placeholder="Manager"
                    />
                    <MyTextInput
                      name="signerName"
                      label="Signer name (auto-generated)"
                      readOnly
                      style={{ backgroundColor: "#f8f8f8" }}
                    />
                  </>
                )}

                {/* POA Section - Only show for Seller roles */}
                {partyIsSeller(selectedRole) && (
                  <>
                    <br />
                    <MyCheckbox
                      name="hasPoa"
                      label="This client will be represented by a Power of Attorney (POA)"
                      onClick={() => setHasPoaField(!hasPoaField)}
                    />
                    {hasPoaField && (
                      <>
                        <Grid>
                           <Grid.Row>
                            <Grid.Column mobile={16} computer={16}>
                              <p>
                                The email above should be for the power of attorney individual.
                                <br/><i>If you want to share documents to your client who the POA represents, add your client to "Other Parties" using the "Other" role and click their button to share documents with them.</i>
                              </p>
                            </Grid.Column>
                            </Grid.Row>
                          <Grid.Row>
                            <Grid.Column mobile={16} computer={8}>
                              <MyTextInput
                                name="poaFullName"
                                label="POA's Full Name"
                                placeholder="Enter POA's full name"
                              />
                            </Grid.Column>
                            <Grid.Column mobile={16} computer={8}>
                              <MyTextInput
                                name="poaTitle"
                                label="POA's Title"
                                placeholder="Enter POA's title"
                              />
                            </Grid.Column>
                          </Grid.Row>
                          <Grid.Row>
                            <Grid.Column width={16}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  marginBottom: "8px",
                                }}
                              >
                                <label style={{ marginRight: "8px" }}>
                                  Signature Display Option
                                </label>
                                <Popup
                                  trigger={
                                    <Icon name="info circle" color="blue" />
                                  }
                                  content={
                                    <div>
                                      <p>
                                        <strong>
                                          Examples of how signatures will
                                          display:
                                        </strong>
                                      </p>
                                      <p>
                                        <strong>Option A:</strong> John Q.
                                        Principal, by Jane R. Agent, as
                                        Attorney-in-Fact
                                      </p>
                                      <p>
                                        <strong>Option B:</strong> Jane R.
                                        Agent, as Attorney-in-Fact for John Q.
                                        Principal
                                      </p>
                                      <p>
                                        <strong>Custom:</strong> You can enter
                                        your own display format
                                      </p>
                                      <br />
                                      <p>
                                        <strong>Note:</strong> The email for
                                        this client should be the POA's email
                                        address.
                                      </p>
                                      <p>
                                        <strong>
                                          Initials will display as:
                                        </strong>{" "}
                                        Client's initials by POA's initials
                                        (AIF)
                                      </p>
                                      <p>
                                        <strong>Example:</strong> JQP by JRA
                                        (AIF)
                                      </p>
                                    </div>
                                  }
                                  wide="very"
                                  position="top center"
                                />
                              </div>
                              <MySelectInput
                                name="poaDisplayOption"
                                options={poaDisplayOptions}
                                placeholder="Select display option"
                              />
                            </Grid.Column>
                          </Grid.Row>
                          {values.poaDisplayOption === "custom" && (
                            <Grid.Row>
                              <Grid.Column width={16}>
                                <MyTextInput
                                  name="poaCustomDisplay"
                                  label="Custom Signature Display"
                                  placeholder="Enter how you want the signature to display"
                                />
                              </Grid.Column>
                            </Grid.Row>
                          )}
                        </Grid>
                      </>
                    )}
                  </>
                )}

                <Divider />
                <FormAddress />
                <Divider className="medium top margin" />
                <Button
                  loading={isSubmitting}
                  disabled={!dirty || isSubmitting}
                  type="submit"
                  floated={isMobile ? null : "right"}
                  primary
                  content="Submit"
                  className={isMobile ? "fluid medium bottom margin" : null}
                />
                <Button
                  disabled={isSubmitting}
                  onClick={() =>
                    dispatch(
                      closeModal({
                        modalType: "TransactionClientAddForm",
                      })
                    )
                  }
                  to="#"
                  type="button"
                  floated={isMobile ? null : "right"}
                  content="Cancel"
                  className={isMobile ? "fluid medium" : null}
                />
              </Form>
            )}
          </Formik>
        </div>
      </Segment>
    </ModalWrapper>
  );
}
